import { clampZoom, type Camera } from '../../lib/camera';
import type { SliceCreator } from '../state';

/**
 * The camera: `{x, y, k}`, applied as one CSS transform on the world and one `<g transform>`
 * on the wire SVG.
 *
 * ITS OWN SLICE FOR A PERFORMANCE REASON, not a tidiness one. A pan or a zoom must re-render
 * neither the node list nor the wire paths — the transform is the only thing that changes, so
 * only whatever renders the transform may subscribe here. The plan states this as an assertion
 * to write, not an aspiration: "assert no node re-renders on pan".
 *
 * TWO BEHAVIOURS CARRIED OVER VERBATIM from the vanilla script, both easy to lose:
 *
 * 1. `setZoomK` NEVER PERSISTS. The vanilla `setZoomK` (infostation.html) deliberately does
 *    not call `save()`; only the day-scoped view record carries the camera, written by
 *    `saveViewState()`. Making the zoom durable would mean a `PUT /api/map` per wheel notch.
 * 2. THE CLAMP IS THE CALLERS', AND IT IS THE SAME EVERYWHERE. The vanilla clamp lives at the
 *    wheel handler, the pinch handler and the view restore — three copies of
 *    `Math.min(2.2, Math.max(0.25, k))`. Here it is `clampZoom`, applied inside `setZoomK`, so
 *    a fourth caller cannot arrive with different bounds. `fit()` is the one place that clamps
 *    differently (it never zooms IN past 1.4), which is why that bound is named separately
 *    rather than folded in.
 */
/**
 * THE SHAPE AND THE BOUNDS LIVE IN `lib/camera.ts` (V0.46), with the framing maths that needs
 * them: "where would the camera land if it framed this box" is pure arithmetic, and it had to
 * become testable without a stage, a DOM or a store before a camera ANIMATION could aim at it.
 * Re-exported from here rather than moved and chased, so every existing import of these four
 * names — `store/index.ts`, `mapController.ts`, `slices.test.ts` — still resolves where it did.
 */
export type { Camera } from '../../lib/camera';
export { MIN_ZOOM, MAX_ZOOM, FIT_MAX_ZOOM, clampZoom } from '../../lib/camera';

export interface CameraSlice {
  view: Camera;

  /** Whole camera at once — a restore, a `fit()`, a `centerOnNodes()`. */
  setView: (next: Camera) => void;
  /** Pan only. Zoom is untouched, so a drag-pan cannot accidentally rescale. */
  panTo: (x: number, y: number) => void;
  /** Zoom only, clamped. Never persisted — see the note above. */
  setZoomK: (k: number) => void;
}

const sameCamera = (a: Camera, b: Camera): boolean => a.x === b.x && a.y === b.y && a.k === b.k;

export const createCameraSlice: SliceCreator<CameraSlice> = (set, get) => ({
  view: { x: 0, y: 0, k: 1 },

  setView: (next) => {
    const clamped: Camera = { x: next.x, y: next.y, k: clampZoom(next.k) };
    if (sameCamera(get().view, clamped)) return;
    set({ view: clamped });
  },

  panTo: (x, y) => {
    const prev = get().view;
    if (prev.x === x && prev.y === y) return;
    set({ view: { x, y, k: prev.k } });
  },

  setZoomK: (k) => {
    const prev = get().view;
    const next = clampZoom(k);
    if (prev.k === next) return;
    set({ view: { x: prev.x, y: prev.y, k: next } });
  },
});
